import { PATHS } from 'Utils/PagePaths';

export const HOSPITALV1_2_DATA = [
  {
    title: "Introduction",
    level: 1,
    content: "The Common Formats for Event Reporting – Hospital V1.2 standardizes how hospitals capture and report patient safety events, near misses, and unsafe conditions. On this page, you'll find V1.2 resources including the data dictionary, user guide, quick start guide, and event-specific forms. You can use the Common Format to design systems that collect data, create reports, and ultimately submit data back to the PSOPPC.",
  },
  {
    title: "Collect Data",
    level: 1,
    content: <><strong>Collect Data from Providers</strong>: Prepare your patient safety event information using the Hospital Version 1.2 Common Format. The resources provided below including the Data Dictionary, Users’ Guide, Forms, and Flow Charts, will help you understand required data elements, answer values, and validation rules before you create your submission files.</>,
    children: [
      {
        title: "Key Resources",
        level: 2,
        cards: [2681, 2741, 2726, 2707, 2680, 2695, 2725, 2729, 2696]
      },
      {
        title: "Event Details",
        level: 2,
        cards: [],
        children: [
          {
            title: "HERF, PIF, and SIR",
            level: 3,
            mainContentTitle: "Healthcare Event Reporting Form (HERF), Patient Information Form (PIF), and Summary Initial Report (SIR)",
            content:
              <>
                <p>The Healthcare Event Reporting Form (HERF) is designed to be completed by the individual who witnessed, first discovered, or is most familiar with the details of the event. The HERF captures information on incidents, near misses, and unsafe conditions and solicits only basic information to start the reporting process.</p>
                <p>The Patient Information Form (PIF) is used to capture essential, non-identifying patient demographic and clinical context related to the event. While the HERF focuses on what happened, the PIF provides information about who was involved from a clinical standpoint such as age group, care setting, and general clinical background, supporting more accurate aggregation and analysis across the national dataset.</p>
                <p>The Summary Initial Report (SIR) builds upon the information collected in the HERF and PIF. It provides a structured summary of the event, including initial assessments, contributing factors, event stage, and the presence or extent of harm. The SIR helps ensure that the event is categorized consistently using the AHRQ Common Formats, enabling comparability across PSOs and providers.</p>
                <p>This three-part structure ensures that each event is reported with the right level of detail, while minimizing burden on frontline staff and supporting consistent, high-quality data.</p>
              </>,
            cardsPrologue: "",
            cards: [2667, 2698, 2714, 2732, 2705, 2712, 2739, 2730]
          },
          {
            title: "Blood or Blood Product",
            level: 3,
            mainContentTitle: "Blood or Blood Product Event",
            content: "A Blood or Blood product event or unsafe condition involves the processing and/or administration of Blood or a Blood product. Patient Safety concerns for this module includes incidents, near-misses, and unsafe conditions that occur in a hospital and do not require that a patient outcome be identified.",
            cardsPrologue: "For more information about the Blood or Blood Product module, please refer to the following documents:",
            cards: [2663, 2678, 2676]
          },
          {
            title: "Device or Medical Surgical Supply, including HIT",
            level: 3,
            mainContentTitle: "Device or Medical Surgical Supply, including HIT Event",
            content: "A device event involves a defect, failure, or incorrect use of a device that results in harm to a patient. (Infections associated with use of a device are described in the Healthcare-associated Infection event description.)",
            cardsPrologue: "For more information about the Device module, please refer to the following documents:",
            cards: [2664, 2687, 2685]
          },
          {
            title: "Fall",
            level: 3,
            mainContentTitle: "Fall Event",
            content: "A fall event involves one or more falls during stay, with or without injury," +
              " whether or not fall was assisted. Fall may be indicated by \"fall\", \"patient found on floor\", or other similar notation. A fall is a sudden, unintended, downward displacement" +
              " of a patient's body to the ground or other object (e.g., onto a bed, chair, or bedside mat). If more than one fall during stay, only most serious event will be captured.",
            cardsPrologue: "For more information about the Fall module, please refer to the following documents:",
            cards: [2665, 2693, 2691]
          },
          {
            title: "Healthcare-associated Infection (HAI)",
            level: 3,
            mainContentTitle: "Healthcare-associated Infection (HAI) Event",
            content: "The Hospital-associated Infection (HAI) module consists of eight different event descriptions which address infections acquired within a hospital that are not present on admission.",
            cardsPrologue: "For more information about the HAI modules, please refer to the following documents:",
            cards: [2666, 2703, 2701]
          },
          {
            title: "Medication or Other Substance",
            level: 3,
            mainContentTitle: "Medication or Other Substance Event",
            content: "An adverse medication event is one in which administration of a medication results in harm to a patient. Included are adverse reactions during or following administration without any apparent incorrect action.",
            cardsPrologue: "For more information about the Medication Product module, please refer to the following documents:",
            cards: [2668, 2711, 2709]
          },
          {
            title: "Perinatal",
            level: 3,
            mainContentTitle: "Perinatal Event",
            content: "A Birth-Perinatal event is an adverse perinatal outcome that occurs immediately before and after birth.",
            cardsPrologue: "For more information about the Perinatal module, please refer to the following documents:",
            cards: [2669, 2719, 2717]
          },
          {
            title: "Pressure Ulcer",
            level: 3,
            mainContentTitle: "Pressure Ulcer Event",
            content: "A pressure injury, also known as a pressure ulcer, is localized damage to the skin and underlying soft tissue usually over a bony prominence or related to a medical or other device.",
            cardsPrologue: "For more information about the Pressure Injury module, please refer to the following documents:",
            cards: [2670, 2724, 2722]
          },
          {
            title: "Surgery or Anesthesia",
            level: 3,
            mainContentTitle: "Surgery or Anesthesia Event",
            content: "A surgery or anesthesia event is an event associated with a surgical procedure in an operating room (OR) or an event associated with the administration of anesthesia or procedural sedation.",
            cardsPrologue: "For more information about the Surgery or Anesthesia module, please refer to the following documents:",
            cards: [2671, 2737, 2735]
          },
          {
            title: "Venous Thromboembolism (VTE)",
            level: 3,
            mainContentTitle: "Venous Thromboembolism Event",
            content: "Venous thromboembolism (VTE) comprises one or both of the following: (1) a deep vein thrombosis (DVT) or (2) a pulmonary embolism (PE).",
            cardsPrologue: "For more information about the Venous Thromboembolism (VTE) module, please refer to the following documents:",
            cards: [2672, 2747, 2745]
          },
          {
            title: "Case Management",
            level: 3,
            content: "",
            cardsPrologue: "",
            cards: [2706, 2740]
          }
        ]
      }
    ]
  },
  {
    title: "Submit Data",
    level: 1,
    content: <><strong>Submit Data to NPSD</strong>: Submit your patient safety event data to the PSOPPC using the Hospital Version 1.2 Common Format. These resources walk you through the process for both CDA XML and Alternate Data Submission formats, including an Implementation Guide, Resources Workbook, and Sample Files. For more information before you get started, see the <a href={PATHS.public['Submit Data']}>Submit Data</a> page.</>,
    children: [
      {
        title: "Key Resources",
        level: 2,
        // 11/03/2025: TODO!!!: double check with the Figma Design
        cards: [2704, 2728, 2042, 2682, 2752, 2750, 2751, 2742]
      },
      {
        title: "Event Details",
        level: 2,
        cards: [],
        children: [
          {
            title: "HERF, PIF, and SIR",
            level: 3,
            mainContentTitle: "Healthcare Event Reporting Form (HERF), Patient Information Form (PIF), and Summary Initial Report (SIR)",
            content:
              <>
                <p>The Healthcare Event Reporting Form (HERF) is designed to be completed by the individual who witnessed, first discovered, or is most familiar with the details of the event. The HERF captures information on incidents, near misses, and unsafe conditions and solicits only basic information to start the reporting process.</p>
                <p>The Patient Information Form (PIF) is used to capture essential, non-identifying patient demographic and clinical context related to the event. While the HERF focuses on what happened, the PIF provides information about who was involved from a clinical standpoint such as age group, care setting, and general clinical background, supporting more accurate aggregation and analysis across the national dataset.</p>
                <p>The Summary Initial Report (SIR) builds upon the information collected in the HERF and PIF. It provides a structured summary of the event, including initial assessments, contributing factors, event stage, and the presence or extent of harm. The SIR helps ensure that the event is categorized consistently using the AHRQ Common Formats, enabling comparability across PSOs and providers.</p>
                <p>This three-part structure ensures that each event is reported with the right level of detail, while minimizing burden on frontline staff and supporting consistent, high-quality data.</p>
              </>,
            cardsPrologue: "",
            cards: [2697, 2713, 2731, 2694]
          },
          {
            title: "Blood or Blood Product",
            level: 3,
            mainContentTitle: "Blood or Blood Product Event",
            content: "A Blood or Blood product event or unsafe condition involves the processing and/or administration of Blood or a Blood product. Patient Safety concerns for this module includes incidents, near-misses, and unsafe conditions that occur in a hospital and do not require that a patient outcome be identified.",
            cardsPrologue: "For more information about the Blood or Blood Product module, please refer to the following documents:",
            cards: [2677, 2675]
          },
          {
            title: "Device or Medical Surgical Supply, including HIT",
            level: 3,
            mainContentTitle: "Device or Medical Surgical Supply, including HIT Event",
            content: "A device event involves a defect, failure, or incorrect use of a device that results in harm to a patient. (Infections associated with use of a device are described in the Healthcare-associated Infection event description.)",
            cardsPrologue: "For more information about the Device module, please refer to the following documents:",
            cards: [2686, 2684]
          },
          {
            title: "Fall",
            level: 3,
            mainContentTitle: "Fall Event",
            content: "A fall event involves one or more falls during stay, with or without injury, whether or not fall was assisted." +
              " Fall may be indicated by \"fall\", \"patient found on floor\", or other similar notation. A fall is a sudden, unintended, downward displacement" +
              " of a patient's body to the ground or other object (e.g., onto a bed, chair, or bedside mat). If more than one fall during stay, only most serious event will be captured.",
            cardsPrologue: "For more information about the Fall module, please refer to the following documents:",
            cards: [2692, 2690]
          },
          {
            title: "Healthcare-associated Infection (HAI)",
            level: 3,
            mainContentTitle: "Healthcare-associated Infection (HAI) Event",
            content: "The Hospital-associated Infection (HAI) module consists of eight different event descriptions which address infections acquired within a hospital that are not present on admission.",
            cardsPrologue: "For more information about the HAI modules, please refer to the following documents:",
            cards: [2702, 2700]
          },
          {
            title: "Medication or Other Substance",
            level: 3,
            mainContentTitle: "Medication or Other Substance Event",
            content: "An adverse medication event is one in which administration of a medication results in harm to a patient. Included are adverse reactions during or following administration without any apparent incorrect action.",
            cardsPrologue: "For more information about the Medication Product module, please refer to the following documents:",
            cards: [2710, 2748]
          },
          {
            title: "Perinatal",
            level: 3,
            mainContentTitle: "Perinatal Event",
            content: "A Birth-Perinatal event is an adverse perinatal outcome that occurs immediately before and after birth.",
            cardsPrologue: "For more information about the Perinatal module, please refer to the following documents:",
            cards: [2718, 2716]
          },
          {
            title: "Pressure Ulcer",
            level: 3,
            mainContentTitle: "Pressure Ulcer Event",
            content: "A pressure injury, also known as a pressure ulcer, is localized damage to the skin and underlying soft tissue usually over a bony prominence or related to a medical or other device.",
            cardsPrologue: "For more information about the Pressure Injury module, please refer to the following documents:",
            cards: [2723, 2721]
          },
          {
            title: "Surgery or Anesthesia",
            level: 3,
            mainContentTitle: "Surgery or Anesthesia Event",
            content: "A surgery or anesthesia event is an event associated with a surgical procedure in an operating room (OR) or an event associated with the administration of anesthesia or procedural sedation.",
            cardsPrologue: "For more information about the Surgery or Anesthesia module, please refer to the following documents:",
            cards: [2736, 2734]
          },
          {
            title: "Venous Thromboembolism (VTE)",
            level: 3,
            mainContentTitle: "Venous Thromboembolism Event",
            content: "Venous thromboembolism (VTE) comprises one or both of the following: (1) a deep vein thrombosis (DVT) or (2) a pulmonary embolism (PE).",
            cardsPrologue: "For more information about the Venous Thromboembolism (VTE) module, please refer to the following documents:",
            cards: [2746, 2744]
          },
        ]
      }
    ]
  }
]